/**
 * Markers superposés au graphique :
 *  - "maintenant" — ligne anthracite discrète + point, masquée si le curseur
 *    est déjà sur cette heure (sinon les 2 markers se superposent et c'est confus).
 *  - "curseur d'exploration" — ligne couleur variable + pastille + pill numérique.
 *    Suit le doigt/souris et synchronise le panneau détail au-dessus.
 */

import { SVG_HEIGHT, SVG_PAD_BOTTOM, SVG_PAD_TOP, VARIABLES } from "./constants.js";
import { PillValeur } from "./MeteoChartPill.js";
import type { MeteoPoint, VariableMeteo } from "./types.js";

export function MeteoChartMarkers({
  hourly,
  indexMaintenant,
  indexCurseur,
  variableActive,
  xFromIndex,
  yFromValue,
  widthMesurée,
}: {
  hourly: MeteoPoint[];
  indexMaintenant: number;
  indexCurseur: number;
  variableActive: VariableMeteo;
  xFromIndex: (i: number) => number;
  yFromValue: (v: number) => number;
  widthMesurée: number;
}) {
  const config = VARIABLES[variableActive];
  const nbPoints = hourly.length;
  const pointActuel = hourly[indexMaintenant];
  const pointCurseur = hourly[indexCurseur];
  const xMaintenant = xFromIndex(indexMaintenant);
  const yMaintenant = pointActuel ? yFromValue(config.extracteur(pointActuel)) : 0;
  const xCurseur = xFromIndex(indexCurseur);
  const yCurseur = pointCurseur ? yFromValue(config.extracteur(pointCurseur)) : 0;
  const valeurCurseur = pointCurseur ? config.extracteur(pointCurseur) : 0;
  const curseurSurMaintenant = indexCurseur === indexMaintenant;

  return (
    <>
      {/* Marker "maintenant" — ligne anthracite + point. */}
      {pointActuel &&
      !curseurSurMaintenant &&
      indexMaintenant >= 0 &&
      indexMaintenant < nbPoints ? (
        <g pointerEvents="none">
          <line
            x1={xMaintenant}
            x2={xMaintenant}
            y1={SVG_PAD_TOP}
            y2={SVG_HEIGHT - SVG_PAD_BOTTOM}
            stroke="#2B2A28"
            strokeWidth={1}
            strokeOpacity={0.45}
          />
          <circle
            cx={xMaintenant}
            cy={yMaintenant}
            r={3}
            fill="#2B2A28"
            fillOpacity={0.55}
            stroke="#fff"
            strokeWidth={1.2}
          />
        </g>
      ) : null}

      {/* Marker "curseur d'exploration" — ligne couleur + pastille + pill. */}
      {pointCurseur && indexCurseur >= 0 && indexCurseur < nbPoints ? (
        <g pointerEvents="none">
          <line
            x1={xCurseur}
            x2={xCurseur}
            y1={SVG_PAD_TOP}
            y2={SVG_HEIGHT - SVG_PAD_BOTTOM}
            stroke={config.couleur}
            strokeWidth={1.6}
            strokeDasharray={curseurSurMaintenant ? "0" : "4 3"}
          />
          <circle
            cx={xCurseur}
            cy={yCurseur}
            r={5}
            fill={config.couleur}
            stroke="#fff"
            strokeWidth={1.8}
          />
          <PillValeur
            xAncre={xCurseur}
            yAncre={yCurseur}
            widthLimite={widthMesurée}
            valeur={valeurCurseur}
            unite={config.unite}
            variableActive={variableActive}
            timeIso={pointCurseur.time}
            couleurFond={config.couleur}
          />
        </g>
      ) : null}
    </>
  );
}
